import type {DatabaseDataResource} from '../DatabaseData/DatabaseDataView';
import type {FreeResourcesOverview} from '../../types/freeResources';
import {formatCreatedDate,formatRelativeDays,formatUptime} from './index';

/**
 * A free VM as a row of the console's instances table (Milos, 2026-11-02: free VMs join the normal table, in their own
 * "Free tier" tab, and work like a normal row with every action but Delete disabled; "via MCP its ok to do everything
 * else").
 *
 * ⛔ NOT A PAID INSTANCE. It comes from GET /v1/free-resources/overview, never from /v1/instances, and never enters the
 *    instances slice: the details page, volume attach, load balancer members, snapshots and backups all act on an
 *    Instance's id, and a free VM has none. Its one id is the free-resource operation id, deliberately named
 *    `operationId` so it can never be passed where an Instance is expected (tests/components/freeVm.types.test.ts makes
 *    that a type error). The API's kind 'website' becomes 'vm' here, so the identifier never reaches the page.
 */
export type FreeVmLifecycle = 'reserved' | 'provisioning' | 'active' | 'held' | 'deleting';
const LIFECYCLES: ReadonlySet<string> = new Set<FreeVmLifecycle>(['reserved', 'provisioning', 'active', 'held', 'deleting']);

export interface FreeVm {
    /** The free-resource operation id: React keys and DELETE /v1/free-resources/console/:operationId. */
    operationId: string;
    kind: 'vm' | 'postgres';
    name: string;
    lifecycle: FreeVmLifecycle;
    /** Reserved and waiting in the free capacity queue. */
    queuedForCapacity: boolean;
    /**
     * Deleting, and a deletion run recorded a stop that no request has cleared since. A deletion request clears any
     * earlier code (a hold, the capacity queue, a previous stop), so on a deleting VM only recordFreeDeletionFailure
     * leaves one.
     */
    deletionStopped: boolean;
    /** The account's free VMs are suspended (a cardless trial ended), so the platform stops them. */
    accountSuspended: boolean;
    size: {vcpus: number; memoryMib: number; diskGib: number};
    createdAt: string | null;
    updatedAt: string | null;
    /** The last start the platform verified (null while the latest verified power event is a stop). */
    startedAt: string | null;
    stoppedAt: string | null;
    image: string | null;
    databaseData: Omit<DatabaseDataResource, 'name'> | null;
    /** The DNS name the VM is published at (its HTTPS address), or null while it is not published. */
    dnsName: string | null;
}

/** One row per occupied slot of an enrolled account. Nothing is invented: a field the API leaves out stays null. */
export const freeVmsFromOverview = (overview: FreeResourcesOverview | null | undefined): FreeVm[] => {
    if (overview?.enrolled !== true) return [];
    return (overview.slots ?? []).flatMap((slot): FreeVm[] => {
        const op = slot.occupied;
        if (!op || !LIFECYCLES.has(op.state)) return [];
        const lifecycle = op.state as FreeVmLifecycle;
        return [{
            operationId: op.id,
            kind: slot.kind === 'postgres' ? 'postgres' : 'vm',
            name: op.name,
            lifecycle,
            queuedForCapacity: lifecycle === 'reserved' && op.error_code === 'free_capacity_wait',
            deletionStopped: lifecycle === 'deleting' && !!op.error_code,
            accountSuspended: overview.suspended === true,
            size: {vcpus: slot.size.vcpus, memoryMib: slot.size.memory_mib, diskGib: slot.size.total_disk_gib},
            createdAt: op.created_at ?? null,
            updatedAt: op.updated_at ?? null,
            startedAt: op.started_at ?? null,
            stoppedAt: op.stopped_at ?? null,
            image: op.image ?? null,
            databaseData: op.database_data ?? null,
            dnsName: op.public_hostname ?? null,
        }];
    });
};

export interface FreeVmStatus {
    label: string;
    /** The progress bar a paid row shows for the same phase. */
    progress: 'building' | 'deleting' | null;
}

/** The Status column, in a paid row's words for the same phase. */
export const freeVmStatus = (vm: FreeVm): FreeVmStatus => {
    switch (vm.lifecycle) {
        case 'deleting':
            return vm.deletionStopped ? {label: 'ERROR', progress: null} : {label: 'DELETING', progress: 'deleting'};
        case 'reserved':
            return vm.queuedForCapacity ? {label: 'Queued for capacity', progress: null} : {label: 'BUILDING', progress: 'building'};
        case 'provisioning':
            return {label: 'BUILDING', progress: 'building'};
        case 'held':
            return {label: 'ERROR', progress: null};
        default:
            if (vm.accountSuspended) return {label: 'SUSPENDED', progress: null};
            if (vm.stoppedAt) return {label: 'SHUTOFF', progress: null};
            return {label: 'ACTIVE', progress: null};
    }
};

/** The fixed size in full, never the internal flavor name: the mobile card and the Flavor column's tooltip. */
export const freeVmSizeLabel = (vm: FreeVm): string =>
    `${vm.size.vcpus} vCPU, ${vm.size.memoryMib / 1024} GiB RAM, ${vm.size.diskGib} GiB disk`;

/** The Flavor column itself: as short as a paid row's flavor name, so the row stays on one line (Milos, 2026-11-02). */
export const freeVmShortSizeLabel = (vm: FreeVm): string => `${vm.size.vcpus} vCPU · ${vm.size.memoryMib / 1024} GiB RAM`;

/** The Image column: the part a person tells VMs apart by, "Ubuntu 24.04" or the database's "PostgreSQL 16". */
const freeVmShortImageLabel = (vm: FreeVm): string =>
    !vm.image ? 'N/A' : vm.kind === 'postgres' ? vm.image.split(', ').pop() ?? vm.image : vm.image;

export interface FreeVmColumns {
    status: FreeVmStatus;
    image: string;
    /** The whole image label: the Image column's tooltip and the mobile card. */
    imageFull: string;
    flavor: string;
    /** The whole size: the Flavor column's tooltip and the mobile card. */
    flavorFull: string;
    created: string;
    updated: string;
    uptime: string;
    state: string;
    task: string;
}

/**
 * Every column but Name and Actions, through the same formatters as a paid row, so a missing value reads as that
 * row's own placeholder. State and Task stay 'N/A': nothing measures a free VM's power state or runs a provider task
 * the console could show, and a shutdown run inside the VM is not recorded, so Uptime is the time since the last start
 * the platform verified (as a paid row's is since its last launch). While the latest verified power event is a stop it
 * reads 'Stopped' whatever the status says: a stopped VM that is being deleted (DELETING, or ERROR when that deletion
 * stopped) has not been running since its stop.
 */
export const freeVmColumns = (vm: FreeVm): FreeVmColumns => {
    const status = freeVmStatus(vm);
    return {
        status,
        image: freeVmShortImageLabel(vm),
        imageFull: vm.image ?? 'N/A',
        flavor: freeVmShortSizeLabel(vm),
        flavorFull: freeVmSizeLabel(vm),
        created: formatCreatedDate(vm.createdAt ?? ''),
        updated: formatRelativeDays(vm.updatedAt ?? ''),
        uptime: vm.stoppedAt && !vm.startedAt ? 'Stopped' : formatUptime(vm.startedAt, status.label),
        state: 'N/A',
        task: 'N/A',
    };
};

/** The heading over the locked actions your agent can do on this VM through redu MCP. */
export const FREE_VM_MCP_HEADING = 'Your agent manages this VM through redu MCP';
/** The heading over the locked actions nothing offers on a free VM: neither the console nor redu MCP. */
export const FREE_VM_UNAVAILABLE_HEADING = 'Not available on a free VM';

/** A Solo row's actions without Delete, in its order (pinned to getInstanceActions by freeVm.test.ts). */
export const FREE_VM_DISABLED_ACTIONS = [
    'Manage DNS', 'Create Snapshot', 'Start Instance', 'Shut Off Instance', 'Soft Reboot Instance', 'Hard Reboot Instance',
    'Resize Instance', 'Confirm Resize', 'Revert Resize', 'Upgrade to Cluster', 'Change Security Group', 'Copy SSH',
] as const;
type FreeVmLockedAction = typeof FREE_VM_DISABLED_ACTIONS[number];

/**
 * The locked actions your agent can do through redu MCP, per VM. For a free VM redu MCP offers a command run inside the
 * VM (exec_free_resource, so a reboot), and on the free VM only, SSH access (get_free_website_ssh) and a published
 * hostname (publish_free_website); the PostgreSQL VM takes commands alone. Nothing offers a free VM a snapshot, a
 * resize, a cluster, a security group change, a hard reboot or a start. Shut Off is left out too: nothing starts a free
 * VM again (no Start anywhere, and the platform restarts only a VM it stopped itself), and a shutdown inside the VM is
 * not recorded, so its row would go on saying ACTIVE.
 */
const THROUGH_MCP: Record<FreeVm['kind'], ReadonlySet<FreeVmLockedAction>> = {
    vm: new Set<FreeVmLockedAction>(['Manage DNS', 'Soft Reboot Instance', 'Copy SSH']),
    postgres: new Set<FreeVmLockedAction>(['Soft Reboot Instance']),
};

export interface FreeVmOption {
    label: string;
    value: string;
    isDisabled?: boolean;
}

export interface FreeVmOptionGroup {
    label: string;
    options: FreeVmOption[];
}

/**
 * The Actions menu: View data first on the PostgreSQL VM (data access, as on the databases page), then a normal row's
 * actions, every one locked under the reason that is true for it on this VM (what redu MCP can do, then what nothing
 * offers on a free VM), then Delete.
 *
 * Delete locks only while its own request runs. A deleting VM keeps it, unlike a paid row: the request is safe to repeat
 * (the same deletion, and a running one answers that it already started), and it is the only way to restart a deletion
 * run that ended without recording a stop (its one-hour limit), which nothing would show.
 */
export const getFreeVmActions = (vm: FreeVm, deleting: boolean): Array<FreeVmOption | FreeVmOptionGroup> => {
    const options: Array<FreeVmOption | FreeVmOptionGroup> = [];
    if (vm.kind === 'postgres') {
        options.push(vm.databaseData?.data_view === true
            ? {label: 'View data', value: 'view_data'}
            : {label: 'View data (not set up)', value: 'view_data', isDisabled: true});
    }
    const locked = (through: boolean): FreeVmOption[] => FREE_VM_DISABLED_ACTIONS
        .filter((label) => THROUGH_MCP[vm.kind].has(label) === through)
        .map((label) => ({label, value: `locked:${label}`, isDisabled: true}));
    for (const [label, through] of [[FREE_VM_MCP_HEADING, true], [FREE_VM_UNAVAILABLE_HEADING, false]] as const) {
        const group = locked(through);
        if (group.length) options.push({label, options: group});
    }
    options.push({label: 'Delete Instance', value: 'delete', isDisabled: deleting});
    return options;
};
